// Pure form-state helpers for the order screen. No React, no I/O.

import type { EyeRx, FieldError, OrderForm } from '@glasses/shared'

const emptyEye = (): EyeRx => ({ sphere: null, cylinder: null, axis: null, prism: null, base: null })

/** A blank form dated `today`; the submission key is minted once and kept across retries. */
export function newForm(today: string, key: string = crypto.randomUUID()): OrderForm {
  return {
    submission_key: key,
    order_date: today,
    participant_name: '',
    member_number: '',
    center_id: null,
    birth_year: null,
    pupillary_distance_mm: null,
    rx_uploaded_to_emr: null,
    frames: {},
    clip_on_id: null,
    od: emptyEye(),
    os: emptyEye(),
    add: null,
    lens_type: null,
  }
}

const without = (frames: OrderForm['frames'], id: string): OrderForm['frames'] => {
  const rest = { ...frames }
  delete rest[id]
  return rest
}

/** Ticking a frame leaves its colour unchosen; unticking removes it (and its colour). */
export function toggleFrame(f: OrderForm, frameId: string, on: boolean): OrderForm {
  return { ...f, frames: on ? { ...f.frames, [frameId]: null } : without(f.frames, frameId) }
}

/** Picking a colour ticks its frame; clicking the chosen colour again removes the frame. */
export function toggleColor(f: OrderForm, frameId: string, color: string): OrderForm {
  return {
    ...f,
    frames: f.frames[frameId] === color ? without(f.frames, frameId) : { ...f.frames, [frameId]: color },
  }
}

export function errorFor(errors: FieldError[], field: string): string | null {
  return errors.find((e) => e.field === field)?.message ?? null
}

export function eyeErrors(errors: FieldError[]): FieldError[] {
  return errors.filter((e) => e.field.startsWith('Od.') || e.field.startsWith('Os.'))
}
